import React from 'react'
import { BaseView, BaseText } from '@/components/base'
import { useSendStore } from '@/store/useSendStore'
import { useGetOnRampPricePerUnit } from '@/domain'
import { useAppPreferencesStore } from '@/store/useAppPreferencesStore'
import { currencyUtils } from '@/utils'
import { extractFiatSymbol } from '@/utils/currency/extractFiatSymbol'
import { Skeleton } from '@/components/Skeleton'
import type { CryptoCode, FiatCode } from '@/constants/currencyCodes'

const { formatCryptoAmount } = currencyUtils

export const SendAmountDisplay: React.FC = () => {
  const isFiatMode = useSendStore((state) => state.isFiatMode)
  const fiatAmount = useSendStore((state) => state.fiatAmount)
  const cryptoAmount = useSendStore((state) => state.cryptoAmount)
  const triggerShake = useSendStore((state) => state.triggerShake)
  const selectedCurrency = useSendStore((state) => state.selectedCurrency)
  const displayCurrency = useAppPreferencesStore(
    (state) => state.displayCurrency,
  )

  // Проверяем состояние загрузки курса валют
  const {
    isLoading: isLoadingOnRampPrice,
    isFetching: isFetchingOnRampPrice,
  } = useGetOnRampPricePerUnit({
    crypto: (selectedCurrency?.code as CryptoCode) || 'USDT',
    fiat: (displayCurrency?.isin as FiatCode) || 'USD',
  })

  const isLoading = isLoadingOnRampPrice || isFetchingOnRampPrice

  const adaptedAmount = isFiatMode ? fiatAmount : cryptoAmount
  const formattedAmount = isFiatMode
    ? adaptedAmount.amount
    : formatCryptoAmount({
        value: Number(adaptedAmount.amount),
        cryptoCode: adaptedAmount.code,
        showDecimals: true,
        showCryptoCode: false,
      })
  // console.log('formattedAmount', formattedAmount)

  // Получаем символ валюты используя универсальную функцию
  const { currencyConditionals } = currencyUtils
  const { isFiduciary, isStablecoin } = currencyConditionals(adaptedAmount.code)

  // Для фиатных валют и стейблкоинов используем символ валюты
  const shouldRenderSymbol = isFiatMode && (isFiduciary || isStablecoin)

  let currencySymbol = ''
  if (shouldRenderSymbol) {
    if (isStablecoin) {
      // Для стейблкоинов используем символ базовой валюты
      const stablecoinMap: Record<string, string> = {
        USDT: 'USD',
        KWDK: 'KWD',
        AEDK: 'AED',
        SARK: 'SAR',
      }
      const baseCurrency =
        stablecoinMap[adaptedAmount.code.toUpperCase()] || 'USD'
      currencySymbol = extractFiatSymbol({ fiatCode: baseCurrency })
    } else {
      currencySymbol = extractFiatSymbol({ fiatCode: adaptedAmount.code })
    }
  }

  // Проверяем, нужно ли показывать символ справа (RTL валюты)
  const isRTLCurrency =
    ['kwd', 'aed', 'sar'].includes(adaptedAmount.code.toLowerCase()) ||
    ['kwdk', 'aedk', 'sark'].includes(adaptedAmount.code.toLowerCase())

  // Формируем отображаемую строку
  const displayAmount = formattedAmount || '0'
  const amountWithSymbol =
    shouldRenderSymbol && currencySymbol
      ? `${!isRTLCurrency ? currencySymbol : ''}${displayAmount}${isRTLCurrency ? ` ${currencySymbol}` : ''}`
      : displayAmount

  // Функция для вычисления размера шрифта в зависимости от длины текста
  const getFontSize = (text: string): number => {
    const length = text.length
    if (length <= 3) return 64
    if (length <= 5) return 56
    if (length <= 7) return 48
    if (length <= 9) return 40
    return 32
  }

  const fontSize = getFontSize(amountWithSymbol)

  // Показываем skeleton loader во время загрузки
  // Используем светлые цвета для skeleton на градиентном фоне
  if (isLoading) {
    return (
      <BaseView center h={60} justify="end">
        <BaseView
          row
          align="baseline"
          mx="auto"
          style={{
            gap: 8,
          }}
        >
          <Skeleton
            viewBox="0 0 200 64"
            backgroundColor="rgba(255, 255, 255, 0.2)"
            foregroundColor="rgba(255, 255, 255, 0.3)"
            style={{
              width: 200,
              height: 64,
            }}
          >
            <rect rx="8" ry="8" width="200" height="64" />
          </Skeleton>
        </BaseView>
      </BaseView>
    )
  }

  return (
    <BaseView center h={60} justify="end">
      {' '}
      {/* Уменьшил высоту с 74 до 60 */}
      <BaseView
        row
        align="baseline"
        mx="auto"
        className={`transition-all duration-300 ${triggerShake ? 'animate-shake' : ''}`}
      >
        <BaseText
          fontSize={fontSize}
          fontFamily="bold"
          color="white"
          style={{
            lineHeight: 1,
            letterSpacing: '-0.02em',
            textShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
          }}
        >
          {shouldRenderSymbol && currencySymbol
            ? amountWithSymbol
            : displayAmount}
        </BaseText>

        {/* Показываем код валюты для криптовалют или когда нет символа */}
        {(!shouldRenderSymbol || !currencySymbol) && (
          <BaseText
            fontSize={18}
            fontFamily="bold"
            color="white"
            style={{
              marginLeft: 8,
              textShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
            }}
          >
            {adaptedAmount.code}
          </BaseText>
        )}
      </BaseView>
    </BaseView>
  )
}
